import {
    Business,
    Cloud,
    Email,
    Fax,
    Home,
    People,
    Phone,
    TrendingUp,
} from '@mui/icons-material';
import { Box, Card, Container, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TechnologiesSection from '../components/TechnologiesSection';


const About = () => {
    const { t } = useTranslation();
    const [activeSection, setActiveSection] = useState('history');
    const [selectedYear, setSelectedYear] = useState('2005');
    const [selectedYear2, setSelectedYear2] = useState('2018');
    const leadership = [
        {
            name: t('about.team.members.mansour.name'),
            role: t('about.team.members.mansour.role'),
            avatar: 'MS',
        },
        {
            name: t('about.team.members.mohammad.name'),
            role: t('about.team.members.mohammad.role'),
            avatar: 'MZ',
        },
        {
            name: t('about.team.members.abbas.name'),
            role: t('about.team.members.abbas.role'),
            avatar: 'AM',
        },
    ];

    const subsidiaries = [
        {
            name: t('about.subsidiaries.ariaIntelligent.name'),
            icon: <TrendingUp sx={{ fontSize: 40 }} />,
            description: t('about.subsidiaries.ariaIntelligent.description'),
            color: '#2196f3',
        },
        {
            name: t('about.subsidiaries.ariaVoice.name'),
            icon: <Business sx={{ fontSize: 40 }} />,
            description: t('about.subsidiaries.ariaVoice.description'),
            color: '#4caf50',
        },
        {
            name: t('about.subsidiaries.sephahanHealth.name'),
            icon: <People sx={{ fontSize: 40 }} />,
            description: t('about.subsidiaries.sephahanHealth.description'),
            color: '#ff9800',
        },
        {
            name: t('about.subsidiaries.robin.name'),
            icon: <Cloud sx={{ fontSize: 40 }} />,
            description: t('about.subsidiaries.robin.description'),
            color: '#9c27b0',
        },
    ];


    const navItems = [
        { id: 'history', label: t('about.navigation.history') },
        { id: 'team', label: t('about.navigation.team') },
        // { id: 'technology', label: t('about.navigation.technology') },
        { id: 'customers', label: t('about.navigation.customers') },
    ];

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navbarHeight = 120; // Height of sticky header + sticky nav menu
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0,
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        navItems.forEach((item) => {
            const element = document.getElementById(item.id);
            if (element) {
                observer.observe(element);
            }
        });

        return () => {
            navItems.forEach((item) => {
                const element = document.getElementById(item.id);
                if (element) {
                    observer.unobserve(element);
                }
            });
        };
    }, []);

    return (
        <Box sx={{ minHeight: '100vh' }}>
            {/* Hero Section - Similar to Contact Us */}
            <Box
                sx={{
                    py: { xs: 6, sm: 8, md: 12 },
                    mt: { xs: 6, sm: 8, md: 10 },
                    px: { xs: 2, sm: 3, md: 4 },
                    background: (theme) =>
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #1e3a5f 0%, #0f2642 100%)'
                            : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            textAlign: 'center',
                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                            fontWeight: 'bold',
                            mb: { xs: 1.5, sm: 2 },
                            color: '#f97316',
                            lineHeight: 1.2,
                        }}
                    >
                        About Demis Holding
                    </Typography>
                </Container>
            </Box>

            {/* Navigation Menu */}
            <Box
                sx={{
                    position: 'sticky',
                    top: { xs: 56, md: 64 },
                    zIndex: 50,
                    backgroundColor: '#f97316',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
            >
                <Container maxWidth="md">
                    <Box
                        sx={{
                            display: 'flex',
                            overflowX: 'auto',
                            flexWrap: { xs: 'nowrap', md: 'wrap' },
                            gap: 0,
                            '&::-webkit-scrollbar': {
                                display: 'none',
                            },
                            scrollbarWidth: 'none',
                        }}
                    >
                        {navItems.map((item) => (
                            <Box
                                key={item.id}
                                onClick={() => scrollToSection(item.id)}
                                sx={{
                                    flex: { xs: '0 0 auto', md: 1 },
                                    px: { xs: 3, md: 4 },
                                    py: 2,
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    backgroundColor: activeSection === item.id ? '#ea580c' : 'transparent',
                                    transition: 'all 0.3s ease',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        backgroundColor: activeSection === item.id ? '#ea580c' : 'rgba(255,255,255,0.1)',
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                    }}
                                >
                                    {item.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* History Section */}
            <Box sx={{
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000000' : 'transparent',
            }}>
                <Container id="history" maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3, md: 6 } }}>
                    {/* Timeline 1: 2005-2016 */}
                    <Box sx={{ mb: 8 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                            overflowX: 'auto',
                            pb: 2,
                            '&::-webkit-scrollbar': {
                                height: 4,
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#374151' : '#e0e0e0',
                                borderRadius: 2,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#f97316',
                                borderRadius: 2,
                            },
                        }}>
                            {[2005, 2009, 2011, 2012, 2014, 2015, 2016].map((year, index) => (
                                <Box key={year} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mr: { xs: 1.5, sm: 2 },
                                    flexShrink: 0,
                                }}>
                                    <Box
                                        onClick={() => setSelectedYear(year.toString())}
                                        sx={{
                                            width: { xs: 36, sm: 40 },
                                            height: { xs: 36, sm: 40 },
                                            borderRadius: '50%',
                                            backgroundColor: selectedYear === year.toString() ? '#f97316' : (theme) => theme.palette.mode === 'dark' ? '#374151' : '#e0e0e0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: selectedYear === year.toString() ? 'white' : (theme) => theme.palette.mode === 'dark' ? '#d1d5db' : '#666',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: selectedYear === year.toString() ? '#ea580c' : (theme) => theme.palette.mode === 'dark' ? '#4b5563' : '#d0d0d0',
                                            },
                                        }}
                                    >
                                        {year}
                                    </Box>
                                    {index < 6 && (
                                        <Box
                                            sx={{
                                                width: { xs: 16, sm: 20 },
                                                height: 2,
                                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#6b7280' : '#e0e0e0',
                                                ml: { xs: 0.5, sm: 1 },
                                                mr: { xs: 0.5, sm: 1 },
                                            }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>

                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 'bold',
                                mb: 3,
                                color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#333',
                            }}
                        >
                            {t(`about.history.timeline1.${selectedYear}.title`)}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.125rem' },
                                lineHeight: 1.8,
                                mb: 3,
                                color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                            }}
                        >
                            {t(`about.history.timeline1.${selectedYear}.description`)}
                        </Typography>

                        {/* Special content for 2011 */}
                        {selectedYear === '2011' && (
                            <Box>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        lineHeight: 1.8,
                                        mb: 2,
                                        color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t(`about.history.timeline1.${selectedYear}.subtitle`)}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        lineHeight: 1.8,
                                        mb: 3,
                                        color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t(`about.history.timeline1.${selectedYear}.subtitle2`)}
                                </Typography>

                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        fontWeight: 'bold',
                                        mb: 2,
                                        color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#333',
                                    }}
                                >
                                    {t(`about.history.timeline1.${selectedYear}.achievements.title`)}
                                </Typography>

                                <Box sx={{ pl: 2, mb: 3 }}>
                                    {t(`about.history.timeline1.${selectedYear}.achievements.items`, { returnObjects: true }).map((item, index) => (
                                        <Typography
                                            key={index}
                                            variant="body2"
                                            sx={{
                                                opacity: 0.8,
                                                mb: 1,
                                                fontSize: '1rem',
                                                color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                            }}
                                        >
                                            • {item}
                                        </Typography>
                                    ))}
                                </Box>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        lineHeight: 1.8,
                                        color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    {t(`about.history.timeline1.${selectedYear}.conclusion`)}
                                </Typography>
                            </Box>
                        )}

                        {/* Special content for 2012 */}
                        {selectedYear === '2012' && (
                            <Box>
                                <Box sx={{ pl: 2, mb: 3 }}>
                                    {t(`about.history.timeline1.${selectedYear}.implementations`, { returnObjects: true }).map((implementation, index) => (
                                        <Typography
                                            key={index}
                                            variant="body2"
                                            sx={{
                                                opacity: 0.8,
                                                mb: 1,
                                                fontSize: '1rem',
                                                color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                            }}
                                        >
                                            • {implementation}
                                        </Typography>
                                    ))}
                                </Box>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        lineHeight: 1.8,
                                        color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    {t(`about.history.timeline1.${selectedYear}.conclusion`)}
                                </Typography>
                            </Box>
                        )}

                        {/* Projects for 2005 */}
                        {selectedYear === '2005' && (
                            <Box sx={{ pl: 2 }}>
                                {t('about.history.timeline1.2005.projects', { returnObjects: true }).map((project, index) => (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{
                                            opacity: 0.8,
                                            mb: 1,
                                            fontSize: '1rem',
                                            color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                        }}
                                    >
                                        • {project}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Timeline 2: 2018-2022 */}
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                            overflowX: 'auto',
                            pb: 2,
                            '&::-webkit-scrollbar': {
                                height: 4,
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#374151' : '#e0e0e0',
                                borderRadius: 2,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#f97316',
                                borderRadius: 2,
                            },
                        }}>
                            {[2018, 2019, 2020, 2021, 2022].map((year, index) => (
                                <Box key={year} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mr: { xs: 1.5, sm: 2 },
                                    flexShrink: 0,
                                }}>
                                    <Box
                                        onClick={() => setSelectedYear2(year.toString())}
                                        sx={{
                                            width: { xs: 36, sm: 40 },
                                            height: { xs: 36, sm: 40 },
                                            borderRadius: '50%',
                                            backgroundColor: selectedYear2 === year.toString() ? '#f97316' : (theme) => theme.palette.mode === 'dark' ? '#374151' : '#e0e0e0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: selectedYear2 === year.toString() ? 'white' : (theme) => theme.palette.mode === 'dark' ? '#d1d5db' : '#666',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: selectedYear2 === year.toString() ? '#ea580c' : (theme) => theme.palette.mode === 'dark' ? '#4b5563' : '#d0d0d0',
                                            },
                                        }}
                                    >
                                        {year}
                                    </Box>
                                    {index < 4 && (
                                        <Box
                                            sx={{
                                                width: { xs: 16, sm: 20 },
                                                height: 2,
                                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#6b7280' : '#e0e0e0',
                                                ml: { xs: 0.5, sm: 1 },
                                                mr: { xs: 0.5, sm: 1 },
                                            }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>

                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 'bold',
                                mb: 3,
                                color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#333',
                            }}
                        >
                            {t(`about.history.timeline2.${selectedYear2}.title`)}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.125rem' },
                                lineHeight: 1.8,
                                mb: 3,
                                color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                            }}
                        >
                            {t(`about.history.timeline2.${selectedYear2}.description`)}
                        </Typography>

                        {/* Special content for 2018 */}
                        {selectedYear2 === '2018' && (
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.125rem' },
                                    lineHeight: 1.8,
                                    color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                }}
                            >
                                {t(`about.history.timeline2.${selectedYear2}.description2`)}
                            </Typography>
                        )}

                        {/* Special content for 2020 */}
                        {selectedYear2 === '2020' && (
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.125rem' },
                                    lineHeight: 1.8,
                                    color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                }}
                            >
                                {t(`about.history.timeline2.${selectedYear2}.description2`)}
                            </Typography>
                        )}

                        {/* Special content for 2022 */}
                        {selectedYear2 === '2022' && (
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.125rem' },
                                    lineHeight: 1.8,
                                    color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                }}
                            >
                                {t(`about.history.timeline2.${selectedYear2}.description2`)}
                            </Typography>
                        )}
                    </Box>
                </Container>

            </Box>

            {/* Team Section */}
            <Box>
                <Container id="team" maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, px: { xs: 3, md: 6 } }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '2rem' },
                            fontWeight: 'bold',
                            mb: { xs: 1.5, md: 2 },
                            textAlign: 'center',
                            color: 'text.primary',
                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                            lineHeight: 1.2,
                        }}
                    >
                        {t('about.team.title')}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.5rem' },
                            fontWeight: 'bold',
                            mb: { xs: 1.5, md: 2 },
                            textAlign: 'center',
                            color: 'text.primary',
                            lineHeight: 1.3,
                        }}
                    >
                        {t('about.team.slogan')}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                            mb: { xs: 4, sm: 5, md: 6 },
                            textAlign: 'center',
                            color: 'text.secondary',
                            maxWidth: '800px',
                            mx: 'auto',
                            lineHeight: { xs: 1.5, md: 1.6 },
                            px: { xs: 2, sm: 0 },
                        }}
                    >
                        {t('about.team.description')}
                    </Typography>

                    <Grid container spacing={{ xs: 3, sm: 4, md: 4 }} justifyContent="center">
                        <Grid item xs={12} sm={6} lg={3}>
                            <Box sx={{ textAlign: 'center', px: { xs: 2, sm: 0 } }}>
                                <Box
                                    component="img"
                                    src="/assets/images/team_saghayi.JPG"
                                    alt={t('about.images.mansour')}
                                    sx={{
                                        width: { xs: 100, sm: 120, md: 120 },
                                        height: { xs: 100, sm: 120, md: 120 },
                                        mx: 'auto',
                                        mb: { xs: 2, sm: 3 },
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    }}
                                />
                                <Typography variant="h6" sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                                    color: 'text.primary',
                                    lineHeight: 1.2,
                                }}>
                                    {t('about.team.members.mansour.name')}
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4,
                                }}>
                                    {t('about.team.members.mansour.role')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Box sx={{ textAlign: 'center', px: { xs: 2, sm: 0 } }}>
                                <Box
                                    component="img"
                                    src="/assets/images/team_hakami.JPG"
                                    alt={t('about.images.shahin')}
                                    sx={{
                                        width: { xs: 100, sm: 120, md: 120 },
                                        height: { xs: 100, sm: 120, md: 120 },
                                        mx: 'auto',
                                        mb: { xs: 2, sm: 3 },
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    }}
                                />
                                <Typography variant="h6" sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                                    color: 'text.primary',
                                    lineHeight: 1.2,
                                }}>
                                    {t('about.team.members.shahin.name')}
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4,
                                }}>
                                    {t('about.team.members.shahin.role')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Box sx={{ textAlign: 'center', px: { xs: 2, sm: 0 } }}>
                                <Box
                                    component="img"
                                    src="/assets/images/team_zahiri.jpg"
                                    alt={t('about.images.mohammad')}
                                    sx={{
                                        width: { xs: 100, sm: 120, md: 120 },
                                        height: { xs: 100, sm: 120, md: 120 },
                                        mx: 'auto',
                                        mb: { xs: 2, sm: 3 },
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    }}
                                />
                                <Typography variant="h6" sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                                    color: 'text.primary',
                                    lineHeight: 1.2,
                                }}>
                                    {t('about.team.members.mohammad.name')}
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4,
                                }}>
                                    {t('about.team.members.mohammad.role')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Box sx={{ textAlign: 'center', px: { xs: 2, sm: 0 } }}>
                                <Box
                                    component="img"
                                    src="/assets/images/team_Malekpor.png"
                                    alt={t('about.images.abbas')}
                                    sx={{
                                        width: { xs: 100, sm: 120, md: 120 },
                                        height: { xs: 100, sm: 120, md: 120 },
                                        mx: 'auto',
                                        mb: { xs: 2, sm: 3 },
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    }}
                                />
                                <Typography variant="h6" sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                                    color: 'text.primary',
                                    lineHeight: 1.2,
                                }}>
                                    {t('about.team.members.abbas.name')}
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4,
                                }}>
                                    {t('about.team.members.abbas.role')}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                </Container>
            </Box>

            {/* Customers Section */}
            <Box>
                {/* Subsidiaries */}
                <Box
                    sx={{
                        py: { xs: 6, md: 8 },
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    }}
                >
                    <Container id="subsidiaries" maxWidth="md" sx={{ px: { xs: 3, md: 6 } }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                                fontWeight: 'bold',
                                mb: { xs: 3, md: 4 },
                                textAlign: 'center',
                                whiteSpace: { xs: 'normal', md: 'nowrap' },
                                color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#333',
                            }}
                        >
                            {t('about.subsidiaries.title')}
                        </Typography>
                        <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                            {subsidiaries.map((subsidiary, index) => (
                                <Grid item xs={12} sm={6} md={6} key={index}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            p: { xs: 2.5, sm: 3, md: 3 },
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                                            borderRadius: 2,
                                            borderTop: `3px solid ${subsidiary.color}`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: `0 8px 24px ${subsidiary.color}44`,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                mb: { xs: 2, sm: 2 },
                                                gap: { xs: 1.5, sm: 2 },
                                                flexDirection: { xs: 'column', sm: 'row' },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    minWidth: { xs: 45, sm: 50 },
                                                    height: { xs: 45, sm: 50 },
                                                    borderRadius: 1.5,
                                                    background: `${subsidiary.color}22`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: subsidiary.color,
                                                    mx: { xs: 'auto', sm: 0 },
                                                }}
                                            >
                                                {subsidiary.icon}
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '1rem', sm: '1.125rem' },
                                                    textAlign: { xs: 'center', sm: 'left' },
                                                    width: '100%',
                                                }}
                                            >
                                                {subsidiary.name}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                opacity: 0.85,
                                                lineHeight: { xs: 1.6, sm: 1.7 },
                                                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                textAlign: { xs: 'center', sm: 'left' },
                                            }}
                                        >
                                            {subsidiary.description}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>
            </Box>

            {/* Technology Section */}
            {/* <Box id="technology">
                <TechnologiesSection />
            </Box> */}

            {/* Customers Image Section */}
            <Box id="customers" sx={{
                py: { xs: 6, md: 8 },
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#111111' : '#f8f9fa',
            }}>
                <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                            fontWeight: 'bold',
                            mb: { xs: 3, md: 4 },
                            textAlign: 'center',
                            color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#333',
                        }}
                    >
                        {t('about.customers.title')}
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                        <Box
                            component="img"
                            src="/assets/images/moshtari.JPG"
                            alt={t('about.images.customers')}
                            sx={{
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: 2,
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                            }}
                        />
                    </Box>
                </Container>
            </Box>

            {/* About Us Section */}
            <Box sx={{
                py: { xs: 6, md: 8 },
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#111111' : '#f8f9fa',
            }}>
                <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
                    <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
                        {/* Left Side - About Us */}
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                                    fontWeight: 'bold',
                                    mb: { xs: 3, md: 4 },
                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#333',
                                    textAlign: { xs: 'center', md: 'left' },
                                }}
                            >
                                {t('about.aboutUs.title')}
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                                    lineHeight: { xs: 1.6, md: 1.8 },
                                    mb: { xs: 3, md: 4 },
                                    color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                    textAlign: { xs: 'center', md: 'left' },
                                }}
                            >
                                {t('about.aboutUs.description1')}
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                                    lineHeight: { xs: 1.6, md: 1.8 },
                                    color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                    textAlign: { xs: 'center', md: 'left' },
                                }}
                            >
                                {t('about.aboutUs.description2')}
                            </Typography>
                        </Grid>

                        {/* Right Side - Way of Communication */}
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                                    fontWeight: 'bold',
                                    mb: { xs: 3, md: 4 },
                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#333',
                                    textAlign: { xs: 'center', md: 'left' },
                                }}
                            >
                                {t('about.communication.title')}
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: 2.5, md: 3 },
                                alignItems: { xs: 'center', md: 'flex-start' },
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: { xs: 1.5, md: 2 },
                                    maxWidth: { xs: '100%', md: 'none' },
                                    width: { xs: '100%', md: 'auto' },
                                }}>
                                    <Home
                                        sx={{
                                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#666',
                                            mt: 0.5,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                                                color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                                lineHeight: { xs: 1.5, md: 1.6 },
                                                textAlign: { xs: 'center', md: 'left' },
                                            }}
                                        >
                                            {t('about.communication.location').split('\n').map((line, index) => (
                                                <span key={index}>
                                                    {line}
                                                    {index < t('about.communication.location').split('\n').length - 1 && <br />}
                                                </span>
                                            ))}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1.5, md: 2 },
                                    maxWidth: { xs: '100%', md: 'none' },
                                    width: { xs: '100%', md: 'auto' },
                                }}>
                                    <Email
                                        sx={{
                                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#666',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                            textAlign: { xs: 'center', md: 'left' },
                                        }}
                                    >
                                        {t('about.communication.emailAddress')}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1.5, md: 2 },
                                    maxWidth: { xs: '100%', md: 'none' },
                                    width: { xs: '100%', md: 'auto' },
                                }}>
                                    <Phone
                                        sx={{
                                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#666',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                            textAlign: { xs: 'center', md: 'left' },
                                        }}
                                    >
                                        {t('about.communication.phone1')}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1.5, md: 2 },
                                    maxWidth: { xs: '100%', md: 'none' },
                                    width: { xs: '100%', md: 'auto' },
                                }}>
                                    <Fax
                                        sx={{
                                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#666',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                            textAlign: { xs: 'center', md: 'left' },
                                        }}
                                    >
                                        {t('about.communication.phone2')}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1.5, md: 2 },
                                    maxWidth: { xs: '100%', md: 'none' },
                                    width: { xs: '100%', md: 'auto' },
                                }}>
                                    <Fax
                                        sx={{
                                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#666',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                                            color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : '#666',
                                            textAlign: { xs: 'center', md: 'left' },
                                        }}
                                    >
                                        {t('about.communication.phone3')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};

export default About;
